import axios from 'axios';
import { isSafeReturnPath } from '@/components/entry/session';
import { REMEMBER_KEY, isDeviceRemembered, refreshRememberedSession } from '@/lib/sessionRefresh';

const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim();
const isBrowser = typeof window !== 'undefined';
const isLocalDevServer = isBrowser && ['3000', '4200'].includes(window.location.port);
const inferredDevBackendUrl = isLocalDevServer ? `${window.location.protocol}//${window.location.hostname}:9010` : '';
const API_URL = backendUrl
  ? `${backendUrl}/api`
  : inferredDevBackendUrl
    ? `${inferredDevBackendUrl}/api`
    : '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Renews a remembered session (the refresh cookie travels only with withCredentials); never goes through the
// interceptors below, so a refused refresh cannot loop.
const refreshClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

const readToken = () => {
  try {
    return localStorage.getItem('token') || '';
  } catch {
    return '';
  }
};

const sentToken = (config) => {
  const header = config?.headers?.Authorization || config?.headers?.authorization || '';
  return String(header).replace(/^Bearer\s+/i, '');
};

/*
  A request answered 401 on a remembered device (01-login #6): the access token ran out before AuthContext renewed it
  (a laptop waking after the 8 h token ended, a phone back from the background). Renew it once and send the request
  again — or simply send it again when another tab / AuthContext already stored a newer token. Resolves with the new
  token, or '' when the device is not remembered (any more) and the person must sign in.
*/
async function renewAfter401(config) {
  if (!config || config._retriedAfterRefresh || !isDeviceRemembered()) return '';
  config._retriedAfterRefresh = true;
  const stored = readToken();
  if (stored && stored !== sentToken(config)) return stored;
  try {
    const data = await refreshRememberedSession(refreshClient);
    localStorage.setItem('token', data.token);
    if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
    if (data.remember_until) localStorage.setItem(REMEMBER_KEY, data.remember_until);
    // AuthContext takes the renewed session over (user, end time) without reloading the page.
    window.dispatchEvent(new CustomEvent('app:session-refreshed', { detail: data }));
    return data.token;
  } catch (refreshError) {
    const status = refreshError?.response?.status;
    if (status === 401 || status === 403) {
      try {
        localStorage.removeItem(REMEMBER_KEY); // the server forgot this device: signed out elsewhere, password changed…
      } catch {
        // storage blocked
      }
    }
    return '';
  }
}

export async function openAuthenticatedFile(fileUrl) {
  const apiPath = String(fileUrl || '').startsWith('/api/')
    ? String(fileUrl).slice(4)
    : String(fileUrl || '');
  const popup = window.open('about:blank', '_blank');
  if (popup) popup.opener = null;
  try {
    const response = await api.get(apiPath, { responseType: 'blob' });
    const objectUrl = URL.createObjectURL(response.data);
    if (popup) {
      popup.location.href = objectUrl;
    } else {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    if (popup) popup.close();
    throw error;
  }
}

/*
  This browser tab's id (one per page load), sent as X-Client-Tab with every request. The server stores it on each
  undo entry, so the shell's Undo toast asks for "the newest change made from THIS tab" (GET /undo/latest?tab=…) and
  never offers a change another tab — or another person signed in on the same account — made a moment later.
*/
const newTabId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};
const CLIENT_TAB_ID = newTabId();
export const getClientTabId = () => CLIENT_TAB_ID;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['X-Client-Tab'] = CLIENT_TAB_ID;
  return config;
});

api.interceptors.response.use(
  (response) => {
    const method = String(response.config?.method || '').toLowerCase();
    const url = String(response.config?.url || '');
    if (
      typeof window !== 'undefined'
      && ['post', 'put', 'patch', 'delete'].includes(method)
      && !url.startsWith('/undo')
      // Marking your own notifications read is personal state, never an undoable change.
      && !url.startsWith('/notifications')
    ) {
      window.dispatchEvent(new CustomEvent('app:mutation-success', { detail: { method, url } }));
    }
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      // A remembered device renews its session and repeats the request instead of reloading on /login.
      const renewed = await renewAfter401(error.config);
      if (renewed) return api(error.config);
      const hadSession = Boolean(localStorage.getItem('token'));
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // A session that ended while someone worked: the sign-in page returns them here (01-login #5). Only an
      // in-app path is ever used (no open redirect); the sign-in page decides "expired" vs "signed out".
      const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.href = hadSession && isSafeReturnPath(here) ? `/login?next=${encodeURIComponent(here)}` : '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
